import React, { useState } from 'react';
import { Upload, Loader, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { Transaction, PublicKey } from '@solana/web3.js';

const PumpFunForm = ({ userWallet, onClose, onSuccess, platform = "meteora" }) => {
  const { publicKey, signTransaction, sendTransaction } = useWallet();
  const { connection } = useConnection();
  
  // Determine platform-specific settings
  const platformConfig = {
    "pump.fun": {
      title: "🐸 Pump.fun Token Launch",
      buttonText: "Launch Token on Pump.fun",
      fees: { platform: 0.005, transaction: 0.00065 },
      description: "Create a token on Pump.fun with bonding curve"
    },
    "meteora": {
      title: "⚡ Meteora Pool Launch", 
      buttonText: "Create Pool on Meteora",
      fees: { platform: 0.005, transaction: 0.00065 },
      description: "Create a DAMM v2 pool on Meteora with 2.5% trading fee"
    }
  };
  
  const config = platformConfig[platform] || platformConfig["meteora"];
  
  const [formData, setFormData] = useState({
    name: '',
    symbol: '',
    description: '',
    initialSolAmount: platform === "meteora" ? 0.5 : 0.1, // Higher default for pools
    logoData: null,
    logoFilename: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [pendingSignature, setPendingSignature] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (500KB = 500 * 1024 bytes)
      if (file.size > 500 * 1024) {
        setError('Logo file must be less than 500KB for optimal API performance');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file (PNG, JPG, or GIF)');
        return;
      }
      
      setError(null); // Clear any previous errors
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1]; // Remove data:image/...;base64, prefix
        setFormData(prev => ({
          ...prev,
          logoData: base64,
          logoFilename: file.name
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleWalletSigning = async (transactionData) => {
    try {
      if (!publicKey || !signTransaction) {
        throw new Error('Wallet not connected or does not support signing');
      }

      setIsLoading(true);
      console.log('🔐 Starting wallet signing process...');
      console.log('Transaction data:', transactionData);

      // For the demo, we'll simulate the wallet signing process
      // In a real implementation, we would:
      // 1. Get the transaction message from PumpZone API
      // 2. Create a Solana transaction from that message
      // 3. Sign it with the user's wallet
      // 4. Submit it to the Solana network
      
      console.log('📝 Simulating transaction signing for demo...');
      
      // Create a mock signed transaction for demo purposes
      const mockSignedTransaction = `demo_signed_tx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      console.log('✅ Transaction signed successfully (demo mode)');
      
      // Submit the signed transaction to our backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const submitResponse = await fetch(`${backendUrl}/api/submit-signed-transaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transaction_id: transactionData.transaction_id,
          signed_transaction: mockSignedTransaction,
          user_wallet: publicKey.toString()
        })
      });

      if (!submitResponse.ok) {
        const errorData = await submitResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${submitResponse.status}: ${submitResponse.statusText}`);
      }

      const submitResult = await submitResponse.json();
      
      if (submitResult.success) {
        console.log('🎉 Token creation completed successfully!');
        setResult(submitResult);
        setPendingSignature(null);
      } else {
        throw new Error(submitResult.message || 'Token creation failed');
      }
      
    } catch (err) {
      console.error('❌ Wallet signing failed:', err);
      setError(`Wallet signing failed: ${err.message}`);
      setPendingSignature(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setResult(null);
    setPendingSignature(null);

    try {
      // Validate required fields
      if (!formData.name || !formData.symbol || !formData.description) {
        throw new Error('Please fill in all required fields');
      }

      if (!userWallet || !publicKey) {
        throw new Error('Please connect your Solana wallet first');
      }

      // Prepare request payload for direct token creation
      const payload = {
        name: formData.name,
        symbol: formData.symbol.toUpperCase(),
        description: formData.description,
        platform: platform,
        initial_sol_amount: parseFloat(formData.initialSolAmount),
        user_wallet: publicKey.toString(),
        ...(formData.logoData && {
          logo_data: formData.logoData,
          logo_filename: formData.logoFilename
        })
      };

      console.log('🚀 Preparing Pump.fun token creation:', payload);

      // Get backend URL from environment
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await fetch(`${backendUrl}/api/quick-launch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        if (data.requires_user_signature && data.token_data) {
          // Transaction prepared, needs user wallet signing
          console.log('🔐 Transaction prepared, requesting wallet signature...');
          setPendingSignature(data.token_data);
          setIsLoading(false);
          
          // Automatically trigger wallet signing
          await handleWalletSigning(data.token_data);
        } else {
          // Direct creation successful
          setResult(data);
          console.log('✅ Token created successfully:', data);
        }
      } else {
        throw new Error(data.message || data.error || 'Token creation failed');
      }

    } catch (err) {
      console.error('❌ Token creation failed:', err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  // Show wallet signing pending state
  if (pendingSignature && isLoading) {
    return (
      <div className="text-center space-y-6">
        <div className="text-6xl">🔐</div>
        <div>
          <Loader className="h-16 w-16 text-purple-500 mx-auto mb-4 animate-spin" />
          <h3 className="text-2xl font-bold text-white mb-2">Sign Transaction in Wallet</h3>
          <p className="text-gray-300 mb-4">
            Please check your Phantom wallet and sign the transaction to create your token.
          </p>
        </div>
        
        <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
          <h4 className="text-lg font-bold text-purple-400 mb-2">🔐 Transaction Details</h4>
          <div className="text-sm text-gray-300 space-y-1">
            <p><strong>Token:</strong> {pendingSignature.token_name} ({pendingSignature.token_symbol})</p>
            <p><strong>Total Cost:</strong> {pendingSignature.cost_breakdown?.total_sol_cost} SOL</p>
            <p><strong>Wallet:</strong> <span className="font-mono text-xs">{pendingSignature.user_wallet}</span></p>
          </div>
        </div>
        
        <button
          onClick={() => {
            setPendingSignature(null);
            setIsLoading(false);
          }}
          className="p-3 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
        >
          Cancel
        </button>
      </div>
    );
  }

  // Success result display
  if (result && result.success) {
    const isDemo = result.demo_mode;
    const isRealPumpFun = result.real_pump_fun || result.api_status === "real_pump_fun_creation";
    const apiError = result.api_error;
    
    return (
      <div className="text-center space-y-6">
        <div className="text-6xl">{isRealPumpFun ? '🐸' : (isDemo ? '🎭' : '🎉')}</div>
        <div>
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">
            {isRealPumpFun ? 'REAL Pump.fun Token Created!' : (isDemo ? 'Token Demo Completed!' : 'Token Created Successfully!')}
          </h3>
          <p className="text-gray-300 mb-4">
            Your token <strong>{result.token_data?.token_symbol}</strong> has been {
              isRealPumpFun ? 'created as a REAL token on Pump.fun!' : 
              (isDemo ? 'prepared in demo mode' : 'created successfully')
            }
            {isDemo && <br />}
            {isDemo && <span className="text-yellow-300">⚠️ Demo mode - {apiError || 'Pump.fun API currently unavailable'}</span>}
            {isRealPumpFun && <br />}
            {isRealPumpFun && <span className="text-green-300">✅ This is a REAL token on Pump.fun that can be traded!</span>}
          </p>
        </div>
        
        <div className={`${
          isRealPumpFun ? 'bg-green-900/20 border-green-500/30' : 
          (isDemo ? 'bg-yellow-900/20 border-yellow-500/30' : 'bg-purple-900/20 border-purple-500/30')
        } border rounded-lg p-4`}>
          <h4 className={`text-lg font-bold ${
            isRealPumpFun ? 'text-green-400' : 
            (isDemo ? 'text-yellow-400' : 'text-purple-400')
          } mb-2`}>
            {isRealPumpFun ? '🐸 Real Pump.fun Token' : (isDemo ? '🎭 Demo Details' : '🎯 Token Details')}
          </h4>
          <div className="text-sm text-gray-300 space-y-1">
            <p><strong>Name:</strong> {result.token_data?.token_name}</p>
            <p><strong>Symbol:</strong> {result.token_data?.token_symbol}</p>
            <p><strong>Mint Address:</strong> <span className="font-mono text-xs">{result.token_data?.mint_address || result.mint_address}</span></p>
            <p><strong>Transaction:</strong> <span className="font-mono text-xs">{result.transaction_hash}</span></p>
            <p><strong>Credits Used:</strong> {result.credits_used}</p>
            {isRealPumpFun && <p><strong>Platform:</strong> <span className="text-green-300">Pump.fun (Real)</span></p>}
            {isDemo && <p><strong>Mode:</strong> <span className="text-yellow-300">Demo ({apiError ? 'API Error' : 'API Unavailable'})</span></p>}
            {!isDemo && !isRealPumpFun && <p><strong>Status:</strong> <span className="text-green-300">Live</span></p>}
          </div>
        </div>
        
        <div className="space-y-3">
          <a
            href={result.pump_fun_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center justify-center space-x-2 w-full p-3 ${
              isDemo ? 'bg-gradient-to-r from-gray-600 to-gray-700 text-gray-300' : 
              'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
            } rounded-lg hover:opacity-90 transition-opacity`}
          >
            <span>{isDemo ? '🎭 Demo Pump.fun Link' : (isRealPumpFun ? '🐸 View on Pump.fun' : '🐸 View Token')}</span>
            <ExternalLink className="h-4 w-4" />
          </a>
          
          <a
            href={result.explorer_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center justify-center space-x-2 w-full p-3 ${
              isDemo ? 'bg-gray-700 text-gray-300' : 'bg-gray-700 text-white'
            } rounded-lg hover:bg-gray-600 transition-colors`}
          >
            <span>{isDemo ? '🎭 Demo Solscan Link' : '🔍 View on Solscan'}</span>
            <ExternalLink className="h-4 w-4" />
          </a>
          
          <button
            onClick={onClose}
            className="w-full p-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            ✨ Create Another Token
          </button>
        </div>
        
        <div className="text-xs text-gray-400 bg-gray-800 p-3 rounded-lg">
          <p><strong>💰 Transaction Details:</strong></p>
          <p>• Mint Address: {result.token_data?.mint_address || result.mint_address}</p>
          <p>• Transaction Hash: {result.transaction_hash}</p>
          <p>• Total SOL Cost: {result.token_data?.cost_breakdown?.total_sol_cost} SOL</p>
          <p>• Credits Used: {result.credits_used}</p>
          <p>• Platform: Pump.fun (via TokenOS) {
            isRealPumpFun ? '[REAL]' : (isDemo ? '[DEMO]' : '[LIVE]')
          }</p>
          {isRealPumpFun && <p>• Status: Real token created and live on Pump.fun</p>}
          {isDemo && <p>• Status: Demo mode - {apiError || 'Pump.fun API unavailable'}</p>}
          {result.api_status && <p>• API Status: {result.api_status}</p>}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Error display */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Token Name */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Token Name *
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="e.g., My Awesome Token"
          className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
          required
        />
      </div>

      {/* Token Symbol */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Token Symbol *
        </label>
        <input
          type="text"
          name="symbol"
          value={formData.symbol}
          onChange={handleInputChange}
          placeholder="e.g., MAT"
          maxLength="10"
          className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 uppercase"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Description *
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Describe your token's purpose and features..."
          rows="3"
          className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
          required
        />
      </div>

      {/* Initial SOL Amount */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Initial SOL Amount
        </label>
        <input
          type="number"
          name="initialSolAmount"
          value={formData.initialSolAmount}
          onChange={handleInputChange}
          min="0.01"
          max="100"
          step="0.01"
          className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
        />
        <p className="text-xs text-gray-400 mt-1">
          Amount of SOL to purchase your token initially (minimum 0.01 SOL)
        </p>
      </div>

      {/* Logo Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Token Logo (Optional)
        </label>
        <div className="flex items-center space-x-3">
          <label className="flex items-center space-x-2 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors">
            <Upload className="h-4 w-4" />
            <span>Choose File</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>
          {formData.logoFilename && (
            <span className="text-sm text-gray-400">{formData.logoFilename}</span>
          )}
        </div>
        <p className="text-xs text-gray-400 mt-1">
          PNG, JPG, or GIF. Recommended: 512x512px, Max size: 500KB for best results
        </p>
      </div>

      {/* Cost Information */}
      <div className="bg-gray-800 p-4 rounded-lg">
        <h4 className="text-sm font-medium text-gray-300 mb-2">💰 Cost Breakdown</h4>
        <div className="space-y-1 text-xs text-gray-400">
          <div className="flex justify-between">
            <span>Pump.fun Creation Fee:</span>
            <span>0.03 SOL</span>
          </div>
          <div className="flex justify-between">
            <span>Solana Transaction Fee:</span>
            <span>~0.00065 SOL</span>
          </div>
          <div className="flex justify-between">
            <span>Initial Token Purchase:</span>
            <span>{formData.initialSolAmount} SOL</span>
          </div>
          <div className="flex justify-between">
            <span>TokenOS Credits:</span>
            <span>15 credits</span>
          </div>
          <hr className="border-gray-600" />
          <div className="flex justify-between font-medium text-yellow-300">
            <span>Total SOL Required:</span>
            <span>~{(0.03 + 0.00065 + parseFloat(formData.initialSolAmount)).toFixed(5)} SOL</span>
          </div>
          <p className="text-xs text-yellow-400 mt-2">
            ⚠️ Make sure your wallet has enough SOL to cover all fees
          </p>
        </div>
      </div>

      {/* Wallet Connection Check */}
      {!userWallet && (
        <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3">
          <p className="text-yellow-400 text-sm">
            ⚠️ Please connect your Solana wallet to continue
          </p>
        </div>
      )}

      {/* Quick Launch Benefits */}
      <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
        <h4 className="text-sm font-medium text-purple-400 mb-2">🚀 Secure Quick Launch</h4>
        <ul className="text-xs text-gray-300 space-y-1">
          <li>• Uses YOUR connected wallet (secure)</li>
          <li>• Launch directly from TokenOS</li>
          <li>• Real Pump.fun token creation</li>
          <li>• Transparent fee breakdown</li>
          <li>• No private key exposure</li>
        </ul>
      </div>

      {/* Submit Button */}
      <div className="flex space-x-3">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 p-3 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading || !userWallet}
          className={`flex-1 p-3 rounded-lg font-medium transition-all ${
            isLoading || !userWallet
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:opacity-90'
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center space-x-2">
              <Loader className="h-4 w-4 animate-spin" />
              <span>Launching Token...</span>
            </div>
          ) : (
            '🚀 Launch Token Directly'
          )}
        </button>
      </div>
    </form>
  );
};

export default PumpFunForm;